import { r as requiredArgs, t as toDate, e as endOfMonth, b as addMonths, a as toInteger, C as ChevronLeftIcon, Y as YearPicker, s as startOfMonth } from './index-23844e78.js';
import * as React from 'react';
import React__default, { useState, useRef, useCallback, useMemo } from 'react';
import { u as useDatePickerDropdown } from './DatePickerDropdownContext-0827e650.js';
import './tslib.es6-18cb2814.js';
import { A as ArrowDownIcon } from './ArrowDownIcon-fbdb97a6.js';
import { C as ChevronRightIcon } from './ChevronRightIcon-b72559d2.js';
import { c as classNames } from './index-8887fc24.js';
import './countries-34fea817.js';
import { B as Button } from './index-b7114926.js';
import { h as FocusTrap } from './index-21f083b4.js';
import { a as MONTH_NAMES, M as MonthPicker } from './index-b6dc1a1e.js';
import { P as Popover } from './index-b7606a36.js';

/**
 * @name startOfDay
 * @category Day Helpers
 * @summary Return the start of a day for the given date.
 *
 * @description
 * Return the start of a day for the given date.
 * The result will be in the local timezone.
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the start of a day
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The start of a day for 2 September 2014 11:55:00:
 * const result = startOfDay(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 02 2014 00:00:00
 */
function startOfDay(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * Days in 1 week.
 *
 * @name daysInWeek
 * @constant
 * @type {number}
 * @default
 */

/**
 * Maximum allowed time.
 *
 * @name maxTime
 * @constant
 * @type {number}
 * @default
 */
var maxTime = Math.pow(10, 8) * 24 * 60 * 60 * 1000;

/**
 * Minimum allowed time.
 *
 * @name minTime
 * @constant
 * @type {number}
 * @default
 */
var minTime = -maxTime;

/**
 * @name isSameDay
 * @category Day Helpers
 * @summary Are the given dates in the same day (and year and month)?
 *
 * @description
 * Are the given dates in the same day (and year and month)?
 *
 * @param {Date|Number} dateLeft - the first date to check
 * @param {Date|Number} dateRight - the second date to check
 * @returns {Boolean} the dates are in the same day (and year and month)
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Are 4 September 06:00:00 and 4 September 18:00:00 in the same day?
 * const result = isSameDay(new Date(2014, 8, 4, 6, 0), new Date(2014, 8, 4, 18, 0))
 * //=> true
 *
 * @example
 * // Are 4 September and 4 October in the same day?
 * const result = isSameDay(new Date(2014, 8, 4), new Date(2014, 9, 4))
 * //=> false
 *
 * @example
 * // Are 4 September, 2014 and 4 September, 2015 in the same day?
 * const result = isSameDay(new Date(2014, 8, 4), new Date(2015, 8, 4))
 * //=> false
 */
function isSameDay(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeftStartOfDay = startOfDay(dirtyDateLeft);
  var dateRightStartOfDay = startOfDay(dirtyDateRight);
  return dateLeftStartOfDay.getTime() === dateRightStartOfDay.getTime();
}

/**
 * @name endOfDay
 * @category Day Helpers
 * @summary Return the end of a day for the given date.
 *
 * @description
 * Return the end of a day for the given date.
 * The result will be in the local timezone.
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the end of a day
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The end of a day for 2 September 2014 11:55:00:
 * const result = endOfDay(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 02 2014 23:59:59.999
 */
function endOfDay(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  date.setHours(23, 59, 59, 999);
  return date;
}

/**
 * @name isLastDayOfMonth
 * @category Month Helpers
 * @summary Is the given date the last day of a month?
 *
 * @description
 * Is the given date the last day of a month?
 *
 * @param {Date|Number} date - the date to check
 * @returns {Boolean} the date is the last day of a month
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Is 28 February 2014 the last day of a month?
 * const result = isLastDayOfMonth(new Date(2014, 1, 28))
 * //=> true
 */
function isLastDayOfMonth(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  return endOfDay(date).getTime() === endOfMonth(date).getTime();
}

/**
 * @name isFirstDayOfMonth
 * @category Month Helpers
 * @summary Is the given date the first day of a month?
 *
 * @description
 * Is the given date the first day of a month?
 *
 * @param {Date|Number} date - the date to check
 * @returns {Boolean} the date is the first day of a month
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Is 1 September 2014 the first day of a month?
 * const result = isFirstDayOfMonth(new Date(2014, 8, 1))
 * //=> true
 */
function isFirstDayOfMonth(dirtyDate) {
  requiredArgs(1, arguments);
  return toDate(dirtyDate).getDate() === 1;
}

/**
 * @name isSameMonth
 * @category Month Helpers
 * @summary Are the given dates in the same month (and year)?
 *
 * @description
 * Are the given dates in the same month (and year)?
 *
 * @param {Date|Number} dateLeft - the first date to check
 * @param {Date|Number} dateRight - the second date to check
 * @returns {Boolean} the dates are in the same month (and year)
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Are 2 September 2014 and 25 September 2014 in the same month?
 * const result = isSameMonth(new Date(2014, 8, 2), new Date(2014, 8, 25))
 * //=> true
 *
 * @example
 * // Are 2 September 2014 and 25 September 2015 in the same month?
 * const result = isSameMonth(new Date(2014, 8, 2), new Date(2015, 8, 25))
 * //=> false
 */
function isSameMonth(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  return dateLeft.getFullYear() === dateRight.getFullYear() && dateLeft.getMonth() === dateRight.getMonth();
}

/**
 * @name isWithinInterval
 * @category Interval Helpers
 * @summary Is the given date within the interval?
 *
 * @description
 * Is the given date within the interval? (Including start and end.)
 *
 * @param {Date|Number} date - the date to check
 * @param {Interval} interval - the interval to check
 * @returns {Boolean} the date is within the interval
 * @throws {TypeError} 2 arguments required
 * @throws {RangeError} The start of an interval cannot be after its end
 * @throws {RangeError} Date in interval cannot be `Invalid Date`
 *
 * @example
 * // For the date within the interval:
 * isWithinInterval(new Date(2014, 0, 3), {
 *   start: new Date(2014, 0, 1),
 *   end: new Date(2014, 0, 7)
 * })
 * //=> true
 *
 * @example
 * // For the date outside of the interval:
 * isWithinInterval(new Date(2014, 0, 10), {
 *   start: new Date(2014, 0, 1),
 *   end: new Date(2014, 0, 7)
 * })
 * //=> false
 *
 * @example
 * // For date equal to interval start:
 * isWithinInterval(date, { start, end: date }) // => true
 *
 * @example
 * // For date equal to interval end:
 * isWithinInterval(date, { start: date, end }) // => true
 */
function isWithinInterval(dirtyDate, interval) {
  requiredArgs(2, arguments);
  var time = toDate(dirtyDate).getTime();
  var startTime = toDate(interval.start).getTime();
  var endTime = toDate(interval.end).getTime();

  // Throw an exception if start date is after end date or if any date is `Invalid Date`
  if (!(startTime <= endTime)) {
    throw new RangeError('Invalid interval');
  }
  return time >= startTime && time <= endTime;
}

/**
 * @name subMonths
 * @category Month Helpers
 * @summary Subtract the specified number of months from the given date.
 *
 * @description
 * Subtract the specified number of months from the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of months to be subtracted. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the months subtracted
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Subtract 5 months from 1 February 2015:
 * const result = subMonths(new Date(2015, 1, 1), 5)
 * //=> Mon Sep 01 2014 00:00:00
 */
function subMonths(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addMonths(dirtyDate, -amount);
}

var styles$1 = {"popover":"Month_popover_cfd27630","month":"Month_month_cfd27630","days":"Month_days_cfd27630","cell":"Month_cell_cfd27630","focus-visible":"Month_focus-visible_cfd27630","dayNames":"Month_dayNames_cfd27630","header":"Month_header_cfd27630","monthControl":"Month_monthControl_cfd27630","monthControlButton":"Month_monthControlButton_cfd27630","monthPickerButton":"Month_monthPickerButton_cfd27630","arrowDown":"Month_arrowDown_cfd27630","controls":"Month_controls_cfd27630","control":"Month_control_cfd27630"};

var monthId = function (instance, id) {
    return "orchard-month-".concat(instance, "-day-").concat(id);
};
var Month = React__default.forwardRef(function Month(props, ref) {
    var boundingRange = props.boundingRange, onPreviousClick = props.onPreviousClick, onNextClick = props.onNextClick, onMonthSelectProp = props.onMonthSelect, onYearSelectProp = props.onYearSelect, _a = props.hideMonthPicker, hideMonthPicker = _a === void 0 ? false : _a, _b = props.hideYearPicker, hideYearPicker = _b === void 0 ? false : _b, showNext = props.showNext, showPrevious = props.showPrevious, numYears = props.numYears, highlightToday = props.highlightToday, hideDaysOutsideMonth = props.hideDaysOutsideMonth, minimizeWeeks = props.minimizeWeeks, id = props.id;
    var _c = useState(undefined), visiblePicker = _c[0], setVisiblePicker = _c[1];
    var nextPickerButton = useRef(null);
    var setVisiblePickerContext = useDatePickerDropdown().setVisiblePicker;
    var buttonRefs = useRef([]);
    var onOpenNextPicker = useCallback(function () {
        if (!hideMonthPicker && visiblePicker !== "month") {
            setVisiblePicker("month");
            setVisiblePickerContext === null || setVisiblePickerContext === void 0 ? void 0 : setVisiblePickerContext("month");
        }
        else if (!hideYearPicker && visiblePicker !== "year") {
            setVisiblePicker("year");
            setVisiblePickerContext === null || setVisiblePickerContext === void 0 ? void 0 : setVisiblePickerContext("year");
        }
    }, [
        hideMonthPicker,
        hideYearPicker,
        visiblePicker,
        setVisiblePickerContext,
    ]);
    var onCloseNextPicker = useCallback(function () {
        if (!hideMonthPicker && visiblePicker === "year") {
            setVisiblePicker("month");
            setVisiblePickerContext === null || setVisiblePickerContext === void 0 ? void 0 : setVisiblePickerContext("month");
        }
        else {
            setVisiblePicker(undefined);
            setVisiblePickerContext === null || setVisiblePickerContext === void 0 ? void 0 : setVisiblePickerContext(null);
        }
    }, [hideMonthPicker, visiblePicker, setVisiblePickerContext]);
    var onMonthSelect = useCallback(function (date) {
        onMonthSelectProp && onMonthSelectProp(date);
        onCloseNextPicker();
    }, [onCloseNextPicker, onMonthSelectProp]);
    var onYearSelect = useCallback(function (date) {
        onYearSelectProp && onYearSelectProp(date);
        onCloseNextPicker();
    }, [onCloseNextPicker, onYearSelectProp]);
    var _d = useMemo(function () {
        var thisDays = props.type === "single" ? props.days : props.rangeMonth.days;
        var thisMonth = props.type === "single" ? props.month : props.rangeMonth.month;
        if (minimizeWeeks && !thisDays[35].inCurrentMonth)
            return { days: thisDays.slice(0, 35), month: thisMonth };
        return { days: thisDays, month: thisMonth };
    }, [minimizeWeeks, props]), days = _d.days, month = _d.month;
    var onKeyDown = useCallback(function (ev) {
        var _a, _b, _c, _d;
        var buttonIdParts = ev.target.id.split("-");
        var dayId = parseInt(buttonIdParts[buttonIdParts.length - 1], 10);
        switch (ev.key) {
            case "ArrowLeft":
                (_a = buttonRefs.current
                    .find(function (b) { return b.id === monthId(id || "0", dayId - 1); })) === null || _a === void 0 ? void 0 : _a.focus();
                break;
            case "ArrowRight":
                (_b = buttonRefs.current
                    .find(function (b) { return b.id === monthId(id || "0", dayId + 1); })) === null || _b === void 0 ? void 0 : _b.focus();
                break;
            case "ArrowUp":
                (_c = buttonRefs.current
                    .find(function (b) { return b.id === monthId(id || "0", dayId - 7); })) === null || _c === void 0 ? void 0 : _c.focus();
                break;
            case "ArrowDown":
                (_d = buttonRefs.current
                    .find(function (b) { return b.id === monthId(id || "0", dayId + 7); })) === null || _d === void 0 ? void 0 : _d.focus();
                break;
        }
    }, [id]);
    var startDate = (props.type === "range" ? props.range : [props.month])[0];
    var monthName = MONTH_NAMES[month.getMonth()];
    var headerText = "".concat(monthName, " ").concat(month.getFullYear());
    var isDayOfRangeMonth = function (thisDay) {
        return thisDay.inRange !== undefined;
    };
    return (React__default.createElement("div", { className: styles$1.month, ref: ref },
        React__default.createElement("div", { className: styles$1.header },
            React__default.createElement(Button, { label: "Previous Month", "data-testid": "month-previous-button", onClick: onPreviousClick, disabled: !showPrevious, variant: "secondary", color: "interface", size: "small" },
                React__default.createElement(ChevronLeftIcon, null)),
            React__default.createElement("div", { className: styles$1.monthControl, "data-testid": "month-month-heading" },
                React__default.createElement(Popover, { isOpen: !!visiblePicker, onClose: onCloseNextPicker, placement: "bottom", content: React__default.createElement(FocusTrap, null,
                        visiblePicker === "year" && (React__default.createElement(YearPicker, { selectedDate: month, onYearSelect: onYearSelect, boundingRange: boundingRange, numYears: numYears })),
                        visiblePicker === "month" && (React__default.createElement(MonthPicker, { selectedDate: month, onMonthSelect: onMonthSelect, onYearSelect: onYearSelect, onOpenYearPicker: onOpenNextPicker, boundingRange: boundingRange, hideYearPicker: hideYearPicker, isPopover: true, numYears: numYears }))) },
                    React__default.createElement(Button, { variant: "secondary", "data-testid": "month-month-button", onClick: onOpenNextPicker, ref: nextPickerButton, color: "interface", size: "small", className: styles$1.monthControlButton, disabled: hideYearPicker && hideMonthPicker },
                        headerText,
                        React__default.createElement(ArrowDownIcon, { className: styles$1.arrowDown })))),
            React__default.createElement(Button, { label: "Next Month", "data-testid": "month-next-button", onClick: onNextClick, disabled: !showNext, variant: "secondary", color: "interface", size: "small" },
                React__default.createElement(ChevronRightIcon, null))),
        React__default.createElement("div", { className: styles$1.dayNames },
            React__default.createElement("div", { className: styles$1.cell }, "Mo"),
            React__default.createElement("div", { className: styles$1.cell }, "Tu"),
            React__default.createElement("div", { className: styles$1.cell }, "We"),
            React__default.createElement("div", { className: styles$1.cell }, "Th"),
            React__default.createElement("div", { className: styles$1.cell }, "Fr"),
            React__default.createElement("div", { className: styles$1.cell }, "Sa"),
            React__default.createElement("div", { className: styles$1.cell }, "Su")),
        React__default.createElement("div", { className: styles$1.days }, days.map(function (day, i) { return (React__default.createElement("button", { ref: function (thisRef) {
                if (thisRef)
                    buttonRefs.current[i] = thisRef;
            }, id: monthId(id || "0", i), "data-testid": "month-day-".concat(day.inCurrentMonth ? "active" : "inactive", "-").concat(day.dateObj.getDate()), "data-active-month": day.inCurrentMonth, "data-outside-bounding-range": !isWithinInterval(day.dateObj, {
                start: boundingRange[0] || minTime,
                end: boundingRange[1] || maxTime,
            }), disabled: !isWithinInterval(day.dateObj, {
                start: boundingRange[0] || minTime,
                end: boundingRange[1] || maxTime,
            }), "data-inrange": isDayOfRangeMonth(day) ? day.inRange : false, "data-start": isDayOfRangeMonth(day) ? day.isStart : false, "data-selected": isDayOfRangeMonth(day)
                ? false
                : isSameDay(day.dateObj, props.value || 0), "data-end": isDayOfRangeMonth(day) ? day.isEnd : false, "data-week-start": day.dayName === "MONDAY", "data-week-end": day.dayName === "SUNDAY", "data-start-of-month": isFirstDayOfMonth(day.dateObj), "data-end-of-month": isLastDayOfMonth(day.dateObj), "data-highlight": highlightToday ? isSameDay(day.dateObj, new Date()) : false, "data-hide-outside-month": hideDaysOutsideMonth, className: styles$1.cell, key: i, onKeyDown: onKeyDown, onClick: function () {
                var displayMonth = props.type === "range" ? props.rangeMonth.month : props.month;
                if (isSameMonth(displayMonth, day.dateObj)) {
                    if (props.type === "range") {
                        if (startDate &&
                            props.isSelectionActive &&
                            day.dateObj >= startDate) {
                            props.setRange([startDate, day.dateObj]);
                            props.setSelectionActive(false);
                            props.onRangeSelect &&
                                props.onRangeSelect([startDate, day.dateObj]);
                        }
                        else {
                            props.setRange([day.dateObj, day.dateObj]);
                            props.setSelectionActive(true);
                        }
                    }
                    else {
                        props.setDate(day.dateObj);
                    }
                }
                else {
                    if (day.dateObj > displayMonth) {
                        onNextClick();
                    }
                    else if (day.dateObj < displayMonth) {
                        onPreviousClick();
                    }
                }
            }, onMouseEnter: function () {
                if (props.type === "range") {
                    if (startDate &&
                        props.isSelectionActive &&
                        day.dateObj >= startDate) {
                        props.setRange([startDate, day.dateObj]);
                    }
                }
            } }, day.dateObj.getDate())); }))));
});

var e=Object.defineProperty,t=Object.defineProperties,n=Object.getOwnPropertyDescriptors,r=Object.getOwnPropertySymbols,o=Object.prototype.hasOwnProperty,a=Object.prototype.propertyIsEnumerable,s=(t,n,r)=>n in t?e(t,n,{enumerable:!0,configurable:!0,writable:!0,value:r}):t[n]=r;const c=Symbol("DayOfMonth"),l=Symbol("DayOfRangeMonth");var g,f;(f=g||(g={})).MONDAY="MONDAY",f.TUESDAY="TUESDAY",f.WEDNESDAY="WEDNESDAY",f.THURSDAY="THURSDAY",f.FRIDAY="FRIDAY",f.SATURDAY="SATURDAY",f.SUNDAY="SUNDAY";const D=[g.SUNDAY,g.MONDAY,g.TUESDAY,g.WEDNESDAY,g.THURSDAY,g.FRIDAY,g.SATURDAY],d=new Map,h=new Map;function m(e,t){return function(e,t){const n=new Date(e.getTime());return n.setDate(t),n}(e,e.getDate()+t)}function y(e,t){const n=e.getMonth()+t,r=e.getFullYear()+Math.floor(n/11),o=new Date(e.getTime());return o.setMonth(n%11),o.setFullYear(r),o}function Y(e){const t=new Date(e.getTime());return t.setDate(1),t.setHours(0,0,0,0),t}function O(e,t){return e.getFullYear()===t.getFullYear()&&e.getMonth()===t.getMonth()&&e.getDate()===t.getDate()}function p(e,t){const n=new Date(e.getTime()),r=new Date(t.getTime());return n.setHours(0,0,0,0),r.setHours(0,0,0,0),n.getTime()>r.getTime()}function b(e,t){const n=new Date(e.getTime()),r=new Date(t.getTime());return n.setHours(0,0,0,0),r.setHours(0,0,0,0),n.getTime()<r.getTime()}function S(e){return `${e.getFullYear()}-${(e.getMonth()+1).toString().padStart(2,"0")}-${(e.getDate()+0).toString().padStart(2,"0")}`}function w(e,t=g.SUNDAY){const n=Y(e),r=D.findIndex((e=>e===t)),o=`${S(n)}-${t}`;if(r<0)throw new RangeError(`Invalid value for day provided: ${t}`);const a=d.get(o);if(a)return a;const s=n.getDay(),u=Array.from({length:42},((e,t)=>{const o=m(n,t-s-(r>s?7:0)+r);return {__type:c,dateObj:o,dayName:D[(t+r)%7],inCurrentMonth:(a=n,u=o,a.getFullYear()===u.getFullYear()&&a.getMonth()===u.getMonth()),ISODateString:S(o)};var a,u;})),i=D.slice(r).concat(D.slice(0,r));return d.set(o,{days:u,daysOfWeek:i,month:n}),{days:u,daysOfWeek:i,month:n}}function A(e,u,i,c=g.SUNDAY){let f=e.map((e=>{if(!(e instanceof Date))throw new TypeError("Given month is not an instance of Date");return `${e.getFullYear()}-${e.getMonth()}`})).join("|");if(f+=`|${u?S(u):""}`,f+=`|${i?S(i):""}`,f+=`|${c}`,h.has(f))return h.get(f);const D=e.reduce(((e,g,f)=>{const{days:D,daysOfWeek:d}=w(g,c);0===f&&(e.daysOfWeek=d.slice(0));const h=D.map((e=>{const c=Boolean(u&&O(e.dateObj,u)),g=Boolean(i&&O(e.dateObj,i));return f=((e,t)=>{for(var n in t||(t={}))o.call(t,n)&&s(e,n,t[n]);if(r)for(var n of r(t))a.call(t,n)&&s(e,n,t[n]);return e})({},e),D={__type:l,inRange:c||g||Boolean(u&&p(e.dateObj,u)&&i&&b(e.dateObj,i)),isStart:c,isEnd:g},t(f,n(D));var f,D;}));return e.months.push({days:h,month:Y(g)}),e}),{months:[],daysOfWeek:[]});return h.set(f,D),D}function T(e=new Date,t){const[n,r]=useState(Y(e)),[o,a]=useState(t||g.SUNDAY),{days:s,daysOfWeek:c}=w(n,o),l=useCallback((()=>{r((e=>Y(y(e,1))));}),[]),f=useCallback((()=>{r((e=>Y(y(e,-1))));}),[]),D=useCallback((e=>{r((t=>Y(function(e,t){if("number"!=typeof t)throw new TypeError(`Expected month to be a number but got ${t}`);if(t<0||t>11)throw new RangeError(`Month must be number between 0 and 11, but got ${t}`);const n=new Date(e.getTime());return n.setMonth(t),n}(t,e))));}),[]),d=useCallback((e=>{r((t=>Y(function(e,t){if("number"!=typeof t)throw new TypeError(`Expected month to be a number but got ${t}`);const n=new Date(e.getTime());return n.setFullYear(t),n}(t,e))));}),[]);return {days:s,daysOfWeek:c,setMonth:r,month:n,setStartOfWeek:a,nextMonth:l,prevMonth:f,setMonthOnly:D,setYearOnly:d}}function M(e,t={}){var n,r;const[o,a]=useState(e),[s,c]=useState((null==t?void 0:t.weekStartsOn)||g.SUNDAY),[l,f]=useState([(null==(n=null==t?void 0:t.range)?void 0:n[0])||null,(null==(r=null==t?void 0:t.range)?void 0:r[1])||null]),{months:D,daysOfWeek:d}=A(o,l[0],l[1],s);return {months:D,daysOfWeek:d,range:l,setMonths:a,setRange:useCallback((e=>{f((t=>{const[n,r]="function"==typeof e?e(t):e;if(n&&r&&b(r,n))throw new RangeError("Range end cannot be before the range start");return [n,r]}));}),[f]),setStartOfWeek:c}}

var styles = {"wrapper":"DatePicker_wrapper_9d0a33a4"};

var DatePicker = React.forwardRef(function DatePicker(_a, ref) {
    var className = _a.className, startDate = _a.startDate, _b = _a.hideMonthPicker, hideMonthPicker = _b === void 0 ? false : _b, _c = _a.hideYearPicker, hideYearPicker = _c === void 0 ? false : _c, value = _a.value, onValueChangeProp = _a.onValueChange, boundingRange = _a.boundingRange, highlightToday = _a.highlightToday, hideDaysOutsideMonth = _a.hideDaysOutsideMonth, minimizeWeeks = _a.minimizeWeeks;
    var _d = React.useState(startDate || new Date()), currentStartDate = _d[0], setCurrentStartDate = _d[1];
    var _e = T(currentStartDate, "MONDAY"), month = _e.month, setMonth = _e.setMonth, days = _e.days;
    var _f = boundingRange || [], minDate = _f[0], maxDate = _f[1];
    React.useEffect(function () {
        setCurrentStartDate(startDate || new Date());
        setMonth(startDate || new Date());
    }, [setMonth, startDate]);
    var isInRange = React.useCallback(function (date) {
        return (minDate ? date >= minDate : true) &&
            (maxDate ? date <= maxDate : true);
    }, [maxDate, minDate]);
    var showPrevious = React.useMemo(function () { return isInRange(endOfMonth(subMonths(currentStartDate, 1))); }, [currentStartDate, isInRange]);
    var showNext = React.useMemo(function () { return isInRange(startOfMonth(addMonths(currentStartDate, 1))); }, [currentStartDate, isInRange]);
    var onPreviousClick = React.useCallback(function () {
        var newMonth = subMonths(currentStartDate, 1);
        setCurrentStartDate(newMonth);
        setMonth(newMonth);
    }, [currentStartDate, setMonth]);
    var onNextClick = React.useCallback(function () {
        var newMonth = addMonths(currentStartDate, 1);
        setCurrentStartDate(newMonth);
        setMonth(newMonth);
    }, [currentStartDate, setMonth]);
    var onMonthSelect = React.useCallback(function (date) {
        var newMonth = new Date(currentStartDate);
        newMonth.setMonth(date.getMonth());
        newMonth.setFullYear(date.getFullYear());
        setCurrentStartDate(newMonth);
        setMonth(newMonth);
    }, [currentStartDate, setMonth]);
    var onYearSelect = React.useCallback(function (date) {
        var newMonth = new Date(currentStartDate);
        newMonth.setFullYear(date.getFullYear());
        setCurrentStartDate(newMonth);
        setMonth(newMonth);
    }, [currentStartDate, setMonth]);
    var onValueChange = React.useCallback(function (newValue) {
        onValueChangeProp && onValueChangeProp(newValue);
        setMonth(newValue);
    }, [onValueChangeProp, setMonth]);
    return (React.createElement("div", { className: classNames(styles.wrapper, className), ref: ref, "data-testid": "date-picker" },
        React.createElement(Month, { type: "single", hideMonthPicker: hideMonthPicker, hideYearPicker: hideYearPicker, showPrevious: showPrevious, showNext: showNext, month: month, days: days, value: value, setDate: onValueChange, boundingRange: boundingRange || [null, null], onPreviousClick: onPreviousClick, onNextClick: onNextClick, onMonthSelect: onMonthSelect, onYearSelect: onYearSelect, highlightToday: highlightToday, hideDaysOutsideMonth: hideDaysOutsideMonth, minimizeWeeks: minimizeWeeks })));
});

export { DatePicker as D, M, Month as a, subMonths as s };
